import { Card, CardBody } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { InfoGrid } from '@/pages/account/components/InfoGrid';
import { formatDate } from '@/lib/format';
import type { EmployeeRecord } from '@/api/employees';

interface Props { emp: EmployeeRecord }

const CONTRACT_LABEL: Record<string, string> = {
  intern: 'Intern',
  probation: 'Probation',
  official: 'Official',
  collaborator: 'Collaborator',
  advisor: 'Advisor',
};

/**
 * Work Info tab — department, position, contract type, seniority, dates,
 * and attendances. Matches the legacy Update/TabComponents/WorkInfo panel.
 */
export function EmployeeTabWorkInfo({ emp }: Props) {
  const attendances = Array.isArray(emp.attendances) ? emp.attendances : [];
  const statusTone = emp.employee_status === 1 ? 'success' : 'danger';
  const statusLabel = emp.employee_status === 1 ? 'Active' : 'Inactive';

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card flat>
        <CardBody>
          <p style={{
            margin: '0 0 var(--vn-space-3)',
            fontSize: 'var(--vn-font-size-sm)',
            fontWeight: 'var(--vn-font-weight-semibold)',
            color: 'var(--vn-fg-default)',
          }}>
            Position
          </p>
          <InfoGrid
            items={[
              { label: 'Department', value: emp.department_name },
              { label: 'Position', value: emp.position_name },
              { label: 'External title', value: emp.employee_external_title },
              {
                label: 'Status',
                value: <StatusPill tone={statusTone}>{statusLabel}</StatusPill>,
              },
            ]}
          />
        </CardBody>
      </Card>

      <Card flat>
        <CardBody>
          <p style={{
            margin: '0 0 var(--vn-space-3)',
            fontSize: 'var(--vn-font-size-sm)',
            fontWeight: 'var(--vn-font-weight-semibold)',
            color: 'var(--vn-fg-default)',
          }}>
            Contract &amp; dates
          </p>
          <InfoGrid
            items={[
              { label: 'Contract type', value: CONTRACT_LABEL[emp.employee_contract_type] ?? emp.employee_contract_type },
              { label: 'Seniority level', value: emp.employee_seniority_level },
              { label: 'Collaborator', value: emp.is_collaborator ? 'Yes' : 'No' },
              { label: 'Start date', value: formatDate(emp.employee_start_date) },
              { label: 'Probation date', value: formatDate(emp.probation_date) },
              { label: 'End date', value: formatDate(emp.employee_end_date) },
              { label: 'Day-off left', value: emp.employee_day_off_left != null ? String(Math.floor(emp.employee_day_off_left)) : '—' },
            ]}
          />
        </CardBody>
      </Card>

      {attendances.length > 0 ? (
        <Card flat>
          <CardBody>
            <p style={{
              margin: '0 0 var(--vn-space-3)',
              fontSize: 'var(--vn-font-size-sm)',
              fontWeight: 'var(--vn-font-weight-semibold)',
              color: 'var(--vn-fg-default)',
            }}>
              Attendances
            </p>
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {attendances.map((a, i) => (
                <div key={i} style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)' }}>
                  <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{a.name}</span>
                  {a.description ? <span style={{ color: 'var(--vn-fg-muted)' }}> — {a.description}</span> : null}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
